/**
 * useHealthCheck — Monitora latência do backend com ping periódico.
 *
 * Faz uma requisição leve (HEAD ao endpoint REST) a cada `intervalMs`.
 * Classifica o estado como: healthy (<1s), slow (1-3s), degraded (>3s), down (timeout/erro).
 *
 * Usage:
 *   const { status, latencyMs } = useHealthCheck();
 */
import { useState, useEffect, useCallback, useRef } from 'react';

export type HealthStatus = 'healthy' | 'slow' | 'degraded' | 'down' | 'offline' | 'checking';

interface UseHealthCheckOptions {
  /** Intervalo entre pings em ms (padrão: 30000 = 30s) */
  intervalMs?: number;
  /** Timeout do ping em ms (padrão: 8000 = 8s) */
  timeoutMs?: number;
  /** Limiar em ms para considerar "lento" (padrão: 2000) */
  slowThresholdMs?: number;
  /** Limiar em ms para considerar "degradado" (padrão: 5000) */
  degradedThresholdMs?: number;
  /** Ativar/desativar (padrão: true) */
  enabled?: boolean;
}

interface UseHealthCheckResult {
  status: HealthStatus;
  latencyMs: number | null;
  lastCheckedAt: Date | null;
  /** Forçar um check imediato */
  checkNow: () => void;
}

export function useHealthCheck({
  intervalMs = 30_000,
  timeoutMs = 8_000,
  slowThresholdMs = 2_000,
  degradedThresholdMs = 5_000,
  enabled = true,
}: UseHealthCheckOptions = {}): UseHealthCheckResult {
  const [status, setStatus] = useState<HealthStatus>('checking');
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;

  const performCheck = useCallback(async () => {
    if (!navigator.onLine) {
      setStatus('offline');
      setLatencyMs(null);
      return;
    }

    if (!supabaseUrl) {
      setStatus('healthy'); // Can't check without URL
      return;
    }

    // Abort previous if still in flight
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const start = performance.now();

    try {
      // Lightweight health check — HEAD request to the REST endpoint
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        method: 'HEAD',
        signal: controller.signal,
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '',
        },
      });

      const elapsed = Math.round(performance.now() - start);
      setLatencyMs(elapsed);
      setLastCheckedAt(new Date());

      if (!response.ok && response.status >= 500) {
        setStatus('down');
      } else if (elapsed >= degradedThresholdMs) {
        setStatus('degraded');
      } else if (elapsed >= slowThresholdMs) {
        setStatus('slow');
      } else {
        setStatus('healthy');
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;

      const elapsed = Math.round(performance.now() - start);
      setLatencyMs(elapsed);
      setLastCheckedAt(new Date());

      if (!navigator.onLine) {
        setStatus('offline');
      } else {
        setStatus('down');
      }
    }
  }, [supabaseUrl, slowThresholdMs, degradedThresholdMs]);

  // Periodic check
  useEffect(() => {
    if (!enabled) return;

    // Initial check after short delay (avoid blocking startup)
    const initialTimer = setTimeout(performCheck, 3_000);
    const interval = setInterval(performCheck, intervalMs);

    // Also check when coming back online
    const handleOnline = () => setTimeout(performCheck, 1_000);
    const handleOffline = () => {
      setStatus('offline');
      setLatencyMs(null);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      abortRef.current?.abort();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [enabled, intervalMs, performCheck]);

  return {
    status,
    latencyMs,
    lastCheckedAt,
    checkNow: performCheck,
  };
}
